<!--
  role3用户专用弹窗组件
  包含用户头像、用户名称和倒计时按钮功能
  支持通过callback参数自定义按钮点击行为，增强组件通用性
-->

<template>
  <div class="msgbox-wrapper role3-toast-wrapper"
       v-show="value"
       :style="restStyle.wrapper">
    <transition name="fade">
      <div :class="['msgbox', {'role3-msgbox': ['role3'].includes(role)}]">
        <!-- 弹窗主体内容 -->
        <div class="msgbox-content">
          <div class="title-wrapper">
            <div class="title">
              续费最高补贴
            </div>
            <div class="sub-title">
              +6.00
            </div>
          </div>
          <!-- 弹窗背景卡片 -->
          <div class="popup-card">
            <!-- 账户信息 -->
            <div class="account-info">
              <span class="label">领取账户</span>
              <div class="user-info">
                <div class="user-avatar">
                  <img :src="userAvatar" alt="用户头像">
                </div>
                <span class="account-name">{{ userName }}</span>
              </div>
            </div>

            <!-- 提示信息 -->
            <div class="tips-row">
              <div class="tip-item">
                <span>发放渠道</span>
              </div>
              <div class="tip-item">
                <span>月卡专享</span>
              </div>
            </div>

            <!-- 倒计时按钮 -->
            <div class="countdown-button-wrapper">
              <div
                class="countdown-button"
                @click.stop="handleButtonClick"
              >
                <span v-if="isCountingDown">开心使用（{{ countdownSeconds }}s）</span>
                <span v-else>开心使用</span>
              </div>
            </div>
          </div>

          <!-- 关闭按钮 -->
          <div
            v-if="showClose"
            class="close-button"
            @click="doClose"
          >
            <img src="https://qncweb.ktvsky.com/20250422/vadd/25adc9123e22d052f2c15bbfe0460227.png" alt="关闭">
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import store from '@/store'
import { utils } from '@/libs/interfaces'
import { newRole4MonthFirstSkuToast7, newRole4MonthFirstSkuToast90 } from '@/libs/utils/consts'

export default {
  name: 'RaiseBoxRole3',

  props: {
    modal: {
      default: false,
    },
  },

  data() {
    return {
      value: false,
      message: '',
      title: '',
      showClose: true,
      restStyle: {},
      callback: null,
      closeCallback: null,
      buttonCallback: null, // 新增：按钮点击回调
      pageType: 'mine_popup_role3', // 新增：页面类型配置
      isCountingDown: false,
      countdownSeconds: 6,
      countdownTimer: null,
    }
  },

  computed: {
    // 用户头像
    userAvatar() {
      return store.state.user_img || 'https://qncweb-back1.ktvsky.com/20221021/other/85220abb50ca0c8ff17e1b4045d563ff.png'
    },

    // 用户名称
    userName() {
      const name = store.state.user_name || '用户'
      // 如果用户名超过6个字符，截取前6个字符并添加省略号
      return name.length > 6 ? `${name.substring(0, 6)}...` : name
    },

    // 用户角色
    role() {
      return store.state.role
    },

    // 判断是否为role4用户（会员过期用户）
    isRole4() {
      return this.role === 'role4'
    },

    // 判断是否为过期7天内的role4用户
    isRole4VipDay() {
      return this.isRole4 && utils.util.checkVipExpiredWithinDays({ $this: this, days: -7 })
    },

    // 判断是否为过期90天以上的role4用户
    isRole4VipDay90() {
      return this.isRole4 && !utils.util.checkVipExpiredWithinDays({ $this: this, days: -90 })
    },

    // 获取过期天数
    expireDays() {
      if (!this.isRole4) return 0

      const now = new Date().getTime()
      const vipEndTime = store.state.vip_endtime * 1000
      const diffDays = Math.floor((now - vipEndTime) / (24 * 60 * 60 * 1000))
      return diffDays > 0 ? diffDays : 0
    },

    // 根据用户角色获取月卡ID
    monthCardId() {
      if (this.isRole4VipDay) {
        return newRole4MonthFirstSkuToast7.memberIdArr[0] // 70
      }
      if (this.isRole4VipDay90) {
        return newRole4MonthFirstSkuToast90.memberIdArr[0] // 31
      }
      return 52 // 默认月卡ID
    },

    // 根据用户角色获取月卡价格
    monthCardPrice() {
      if (this.isRole4VipDay) {
        return newRole4MonthFirstSkuToast7.price[0] // 1690
      }
      if (this.isRole4VipDay90) {
        return newRole4MonthFirstSkuToast90.price[0] // 1390
      }
      return 1490 // 默认月卡价格
    },
  },

  methods: {
    // 显示弹窗
    show(options = {}) {
      this.message = options.message || ''
      this.title = options.title || ''
      this.callback = options.callback || null
      this.closeCallback = options.closeCallback || null
      this.buttonCallback = options.buttonCallback || null // 设置按钮点击回调
      this.pageType = options.pageType || 'mine_popup_role3' // 设置页面类型
      this.restStyle = options.restStyle || {}
      this.showClose = options.showClose !== false
      this.value = true

      // 弹窗展示时自动开始倒计时
      this.startCountdown()

      // 打点：弹窗展示
      this.$sendLog({
        event_type: 'show',
        event_name: 'role3用户专属弹窗展示',
        event_data: {
          role: this.role,
          pageType: this.pageType,
        },
      })
    },

    // 关闭弹窗
    doClose() {
      this.value = false
      this.clearCountdown()

      if (this.closeCallback) {
        this.closeCallback()
      }
    },

    // 点击遮罩
    handleMaskClick() {
      if (this.modal) {
        this.doClose()
      }
    },

    // 点击弹窗内容
    handleAction() {
      // 可以在这里添加点击弹窗内容的逻辑
    },

    // 点击按钮
    handleButtonClick() {
      // 优先执行按钮专用回调，如果没有则执行通用回调
      if (this.buttonCallback && typeof this.buttonCallback === 'function') {
        this.buttonCallback()
      } else if (this.callback && typeof this.callback === 'function') {
        this.callback()
      }
    },

    // 开始倒计时
    startCountdown() {
      this.isCountingDown = true
      this.countdownSeconds = 6

      this.countdownTimer = setInterval(() => {
        this.countdownSeconds--

        if (this.countdownSeconds <= 0) {
          this.clearCountdown()
        }
      }, 1000)
    },

    // 清除倒计时
    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
      this.isCountingDown = false
      this.countdownSeconds = 6
    },

    // 调起支付（保留作为默认实现，但现在主要通过callback调用）
    triggerPayment() {
      // 关闭当前弹窗
      this.doClose()

      // 根据用户角色获取正确的月卡ID和价格
      const memberId = this.monthCardId
      const pay_fee = this.monthCardPrice

      // 调起月卡支付
      utils.util.buyVipPlus({
        memberId, // 根据用户角色动态设置月卡套餐ID
        pay_fee, // 根据用户角色动态设置支付金额（分）
        $this: this,
        pay_enter: 'mine_popup_role3',
      })

      // 发送支付点击日志
      this.$sendLog({
        event_type: 'click',
        event_name: 'role3弹窗支付点击',
        event_data: {
          role: this.role,
          isRole4VipDay: this.isRole4VipDay,
          isRole4VipDay90: this.isRole4VipDay90,
          memberId,
          pay_fee,
          pageType: this.pageType,
        },
      })
    },
  },

  beforeDestroy() {
    this.clearCountdown()
  },
}
</script>

<style lang="scss" scoped>
.role3-toast-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.msgbox {
  position: relative;
  width: 750px;
  height: 910px;
  background: url('https://qncweb.ktvsky.com/20251204/vadd/4613f985d8c815ad15cffc1423adf6e0.png') no-repeat center center;
  background-size: contain;
}

.role3-msgbox {
  position: relative;
  width: 750px;
  height: 910px;
  background: url('https://qncweb.ktvsky.com/20250925/vadd/3c3457c4a3bc9935b83d64cf703e81a5.png') no-repeat center center;
  background-size: contain;
}

.msgbox-content {
  position: relative;
  width: 526px;
  height: 669px;
  margin: 162px auto 0;
  .title-wrapper {
    position: absolute;
    top: 176px;
    left: 0;
    width: 100%;
    text-align: center;
    .title {
      font-size: 24px;
      color: #fff;
    }
    .sub-title {
      font-size: 68px;
      font-weight: 600;
      color: #fff;
    }
  }
  .popup-card {
    padding: 400px 38px 20px;
    color: #AAAAAA;
    text-align: center;
    position: relative;
    font-size: 24px;

    .account-info {
      height: 40px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 30px;

      .user-info {
        display: flex;
        align-items: center;
        gap: 8px;

        .user-avatar {
          display: flex;
          width: 33px;
          height: 33px;

          img {
            width: 100%;
            height: 100%;
            border-radius: 50%;
          }
        }

        .account-name {
          color: #333333;
          max-width: 300px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }

    .tips-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 49px;
      font-size: 24px;

      .tip-item {
        display: flex;
        align-items: center;
        opacity: 0.8;
      }
    }

    .countdown-button-wrapper {
      .countdown-button {
        width: 450px;
        height: 80px;
        line-height: 80px;
        background: linear-gradient(180deg, #FF0053 0%, #FF3C7B 100%);
        color: #ffffff;
        border: none;
        border-radius: 8px;
        font-size: 28px;
        font-weight: bold;
        cursor: pointer;
      }
    }
  }

  .close-button {
    position: fixed;
    top: 20px;
    right: 30px;
    width: 54px;
    height: 54px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1002;
  }
}

// 过渡动画
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
